import { useMemo } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { Logo } from "@/components/journey/TopBar";
import Tm from "@/components/journey/Tm";
import { mediaSrc } from "@/lib/api";

export default function StepHero() {
  const { settings, state, update, pick, lang } = useApp();
  const tx = settings?.texts || {};
  const photo = useMemo(() => mediaSrc(settings?.media?.hero || settings?.media?.photo || settings?.media?.logo), [settings]);
  const paragraphs = (pick(tx, "hero_story") || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const quote = pick(tx, "hero_quote");
  const cta = pick(tx, "hero_cta") || "Get Started";
  const tagline = pick(tx, "hero_tagline") || "Quality. Precision. Compassion.";

  const setLang = (l) => update({ lang: l });

  return (
    <section className="glass space-y-5 rounded-[2rem] p-5 sm:space-y-7 sm:p-10" data-testid="step-hero">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Logo size="h-14 w-14" />
          <div>
            <p className="font-display text-2xl font-semibold tracking-[0.08em] text-white sm:text-3xl md:text-4xl">
              JOYFUL MINDS<Tm sizeEm={0.5} />
            </p>
            <p className="mt-1 text-xs text-amber-200/90">{tagline}</p>
          </div>
        </div>
        <div className="flex items-center gap-1 rounded-full border border-emerald-300/20 bg-black/25 p-1 text-xs" data-testid="hero-lang-switch">
          <button data-testid="hero-lang-en" onClick={() => setLang("en")} className={`rounded-full px-3 py-1 ${lang === "en" ? "bg-emerald-400 text-emerald-950" : "text-emerald-100/70"}`}>EN</button>
          <button data-testid="hero-lang-ml" onClick={() => setLang("ml")} className={`rounded-full px-3 py-1 ${lang === "ml" ? "bg-emerald-400 text-emerald-950" : "text-emerald-100/70"}`}>മലയാളം</button>
        </div>
      </div>

      <div className="grid gap-6 sm:grid-cols-[220px,1fr]">
        {photo && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9 }}
            className="relative mx-auto h-48 w-48 overflow-hidden rounded-3xl ring-2 ring-amber-200/30 sm:h-full sm:w-full"
          >
            <img src={photo} alt="Joyful Minds" className="h-full w-full object-cover" />
          </motion.div>
        )}
        <div className="space-y-3 text-emerald-50/90">
          {paragraphs.length ? paragraphs.map((p, i) => (
            <motion.p key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 * i, duration: 0.6 }}
              className="text-sm leading-relaxed sm:text-base" data-testid={`hero-story-${i}`}>
              {p}
            </motion.p>
          )) : (
            <p className="text-sm text-emerald-100/60">Add the hero story in Admin → Doctor, fees & texts → Hero story.</p>
          )}
        </div>
      </div>

      {quote && (
        <blockquote className="rounded-2xl border border-amber-200/25 bg-amber-100/[0.05] p-5 font-display text-lg italic leading-relaxed text-amber-100 sm:p-6" data-testid="hero-quote">
          {quote}
        </blockquote>
      )}

      <div className="flex justify-center">
        <Button onClick={() => update({ step: 1 })} data-testid="hero-get-started-btn"
                className="h-12 rounded-full bg-emerald-400 px-10 text-base text-emerald-950 transition-transform hover:-translate-y-0.5 hover:bg-emerald-300">
          {cta} <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </section>
  );
}
